import ContentGrid from "../../../../components/ContentGrid/component";
import Ad from "../../../../components/Ad/component";
import { getLayout } from "../../../../components/ContentGrid/utils/mapping";
import { DEFAULT_KEY } from "../../constants";
import { CARD_TYPES } from "../../../../components/Card/constants";
import { SectionHeadingContainer } from "../../components/SectionHeadingContainer/component";

/**
 * This function handles creating the proper component structure for an adelement
 *
 * @param {Object} params the section params
 * @param {number} sectionIndex the index of the current section
 *
 * Key properties of the @param params object
 * @param {string} params.adType the type of ad that will render
 * @param {number} params.sectionIndex the index of the current section
 * @param {string} params.numberOfAds the # of ads to display (only takes effect for native-ad type)
 * @param {string} params.title heading title of the ad element section
 * @param {string} params.imageUrl sponsor logo url
 * @param {string} params.imageTitle sponsor logo image title
 * @param {boolean} params.lazyLoad ad lazy loading on/off flag
 *
 * @returns {JSX} component structure for an Ad Element
 */

const AdElementContainer = ({ params, sectionIndex, viewport }) => {
    if (!params) {
        return null;
    }
    const { adType, numberOfAds, title, imageUrl, imageTitle, lazyLoad, name } =
        params;

    if (!adType || sectionIndex === undefined) {
        return null;
    }

    //this makes sure the correct plc value is given to the right rail bigbox ads
    const plc =
        (adType === "bigbox") & (viewport === "viewportLarge")
            ? "right"
            : "feed";

    if (adType === "native-ad" && name !== "Homepage redesign native ad") {
        const heading = (
            <SectionHeadingContainer
                title={title}
                image={imageUrl}
                imageAlt={imageTitle || ""}
            />
        );
        const containerLayout = getLayout(DEFAULT_KEY);

        const adItems = [...Array(numberOfAds ? numberOfAds : 1)].map(() =>
            createAdContentData({ params: { ...params, sectionIndex } })
        );

        return (
            <>
                <div className="flexibleLandingPage-sectionHeading-sponsored">
                    {heading}
                </div>
                <ContentGrid gridLayout={containerLayout} gridData={adItems} />
            </>
        );
    } else {
        return (
            <Ad
                type={adType}
                targeting={{ plc: plc, sectionIndex }}
                lazy={lazyLoad}
            />
        );
    }
};

/**
 * This function handles creating the proper data object to create ad components
 *
 * @param {Object} params the ad params
 *
 * Key properties of the @param params object
 * @param {string} params.type ad type
 * @param {number} params.sectionIndex index of the section the ad is being rendered in
 *
 * @returns {Object} ad component data object
 */
export function createAdContentData({ params }) {
    if (!params || !params.type || params.sectionIndex === undefined) {
        return;
    }
    const key = params.type + "-" + params.sectionIndex;
    const adData = {
        key,
        type: params.type,
        targeting: {
            plc: "feed",
            sectionIndex: params.sectionIndex,
        },
        cardType: CARD_TYPES.TYPE_REGULAR,
    };
    return adData;
}

/**
 * This function handles injecting an native-ad component into section
 *
 * @param {Object} params the ad params and content items
 *
 * Key properties of the @param params object
 * @param {Array} params.content list of content items
 * @param {number} params.sectionIndex index of the section the ad is being rendered in
 * @param {boolean} params.isNativeAd is undefined on initial load, will then return true/false from macaw
 *
 * @returns {Object} gridData
 */
export function injectNativeAds({ params }) {
    // Do not re-insert Ad on re-render when Ads failed to return ie. isNativeAd = false
    if (
        !params ||
        !params.gridData ||
        params.sectionIndex === undefined ||
        !params.isNativeAd
    ) {
        return null;
    }

    const AD_POSITION = 5;
    const index = params.sectionIndex;
    let gridData = [...params.gridData];

    // Insert Ad only on first section and when Ads position less than number of cards
    if (index === 0 && AD_POSITION < gridData.length - 1) {
        gridData.splice(
            AD_POSITION,
            0,
            createAdContentData({
                params: {
                    type: "native-ad",
                    sectionIndex: index,
                    nativeAdCallback: params.nativeAdCallback,
                },
            })
        );
    }

    return gridData;
}

export default AdElementContainer;
